import { formatCurrency } from "../../js/shared.966473804198.js";

const calculatorContentElement = document.getElementById("calculator-content");
const calculatorContent = calculatorContentElement
  ? JSON.parse(calculatorContentElement.textContent)
  : {};

const itemizedCostRow = (content, value) => {
  const label = content.tooltip
    ? `
    <tqc-tooltip>
      <dfn class="pv-text-body-xs">${content.label}</dfn>
      <div role="tooltip">
        <p>${content.tooltip}</p>
      </div>
    </tqc-tooltip>
  `
    : `<div class="pv-text-body-xs">${content.label}</div>`;

  return `
<div class="pv-text-body-xs pv-space-between">
    ${label}
    <div class="pv-text-body-xs tqc-pet-calculator-result">${content.percentage ? `${value}%` : formatCurrency(value)}</div>
</div>`;
};

const contentByFieldName = {
  "oop-max": {
    label: "Out-of-pocket-max remaining",
    tooltip: calculatorContent.oop_max_tooltip,
  },
  deductible: {
    label: "Deductible remaining",
    tooltip:
      "The amount you have to pay before your insurance begins to cover a portion of the cost of this service.",
  },
  copay: {
    label: "Co-pay",
  },
  coinsurance: {
    label: "Coinsurance",
    percentage: true,
  },
};

class PetCalculatorController {
  constructor() {
    this.elements = {
      // Display elements
      rateChartAnnotation: document.querySelector(
        ".tqc-rate-chart__annotation",
      ),

      // Display elements that come in multiples due to the responsive layout
      resetButtons: document.querySelectorAll(
        "[data-action='reset-pet-calculator']",
      ),
      // The big calculator CTA with dynamic text
      mainButtons: document.querySelectorAll(
        ".tqc-pet-calculator__main-button",
      ),
      // All buttons that toggle calculator (incl. main CTA and "modify" button)
      calculatorToggles: document.querySelectorAll(
        "[data-action='toggle-pet-calculator']",
      ),
      calculatorResultSummaries: document.querySelectorAll(
        ".tqc-pet-calculator-result",
      ),

      // The calculator itself
      modal: document.getElementById("calculator-pet-modal"),
      calculatorForm: document.querySelector("cost-share-calculator-form"),
    };
    this.totalPrice = parseFloat(
      this.elements.calculatorForm.getAttribute("data-total-price"),
    );

    // Store original button text for reset
    this.originalButtonText =
      this.elements.mainButtons[0]?.querySelector("span")?.textContent ||
      "Calculate how much insurance will cover";

    this.bindEvents();
  }

  bindEvents() {
    this.elements.calculatorToggles.forEach((button) => {
      button.addEventListener("click", this.openModal);
    });

    this.elements.resetButtons.forEach((button) => {
      button.addEventListener("click", this.dispatchReset);
    });
    document.addEventListener("tqc:calculator-submit", this.handleSubmit);
    document.addEventListener("tqc:calculator-reset", this.handleReset);
  }

  dispatchReset = () => {
    document.dispatchEvent(new CustomEvent("tqc:calculator-reset"));
  };

  openModal = () => {
    if (this.elements.modal) {
      this.elements.modal.showModal();
    }
  };

  updateYourCostsSection = (formData) => {
    this.resetYourCostsSection();

    this.elements.mainButtons.forEach((button) => {
      button.classList.add("pv-hide");
    });

    this.elements.calculatorResultSummaries.forEach((summary) => {
      summary.classList.remove("pv-hide");

      const costInputSection = summary.querySelector(".tqc-your-costs");
      if (formData.get("oop-max-radio") === "yes") {
        costInputSection.insertAdjacentHTML(
          "beforeend",
          itemizedCostRow(contentByFieldName["oop-max"], 0),
        );
        return;
      }
      for (const [name, value] of formData.entries()) {
        const content = contentByFieldName[name];
        if (content && value !== "") {
          costInputSection.insertAdjacentHTML(
            "beforeend",
            itemizedCostRow(content, value),
          );
        }
      }
    });
  };

  resetYourCostsSection = () => {
    this.elements.mainButtons.forEach((button) => {
      button.classList.remove("pv-hide");
    });

    this.elements.calculatorResultSummaries.forEach((summary) => {
      summary.classList.add("pv-hide");
      const items = summary.querySelectorAll(".tqc-your-costs div");
      for (let item of items) {
        item.remove();
      }
    });
  };

  insertAnnotation = (amountLabel) => {
    this.elements.rateChartAnnotation.insertAdjacentHTML(
      "beforeend",
      `<div class="tqc-you-pay">You pay: ${amountLabel}</div>`,
    );
  };

  resetAnnotation = () => {
    const annotationSubscript =
      this.elements.rateChartAnnotation.querySelector(".tqc-you-pay");
    if (annotationSubscript) {
      annotationSubscript.remove();
    }
  };

  updateLabels = () => {
    document.querySelectorAll("[data-before-coverage]").forEach((element) => {
      element.outerHTML = "<span data-before-coverage>with</span>";
    });
    this.elements.mainButtons.forEach((button) => {
      const buttonLabel = button.querySelector("span");
      buttonLabel.textContent = "Calculator estimate breakdown";
      const buttonCaret = button.lastElementChild;
      buttonCaret.classList.remove("pv-hide");
    });
  };

  updateTotalAmount = (amountLabel) => {
    const priceLabels = document.querySelectorAll("[data-original-price]");
    priceLabels.forEach((el) => {
      el.textContent = amountLabel;
    });

    const whatYourInsuranceCovers = document.querySelectorAll(
      "[data-insurance-covers]",
    );
    whatYourInsuranceCovers.forEach((el) => {
      const amountWithoutDollar = amountLabel.slice(1).replace(",", "");
      el.textContent = formatCurrency(
        parseInt(amountWithoutDollar) - this.totalPrice,
      );
    });
  };

  closeModal = () => {
    if (this.elements.modal) {
      this.elements.modal.close();
    }
  };

  handleSubmit = (e) => {
    this.resetAnnotation();
    this.insertAnnotation(e.detail.currencyPatientCost);

    this.updateLabels();

    this.updateYourCostsSection(e.detail.formData, e.detail.roundedCost);

    this.updateTotalAmount(e.detail.currencyPatientCost);

    this.closeModal();
  };

  resetTotalAmount = () => {
    const priceLabels = document.querySelectorAll("[data-original-price]");
    priceLabels.forEach((el) => {
      el.textContent = formatCurrency(el.dataset.originalPrice);
    });
  };

  resetLabels = () => {
    document.querySelectorAll("[data-before-coverage]").forEach((element) => {
      element.outerHTML = "<strong data-before-coverage>before</strong>";
    });
    this.elements.mainButtons.forEach((button) => {
      const buttonLabel = button.querySelector("span");
      buttonLabel.textContent = this.originalButtonText;
      const buttonCaret = button.lastElementChild;
      buttonCaret.classList.add("pv-hide");
    });
  };

  handleReset = (e) => {
    this.resetYourCostsSection();
    this.resetTotalAmount();
    this.resetLabels();
    this.resetAnnotation();
  };
}

new PetCalculatorController();
